import { Eye, EyeOff } from 'lucide-react';
import React, { FC, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useHeroToast } from '../../hooks';
import { useAuthState } from '../../state';
import {
  FormContainer,
  InfoText,
  LoadingSpinner,
  LoginBox,
  LoginContent,
  LoginHeader,
  MainContainer,
  WelcomeText,
} from './SignIn.styled';

const REMEMBER_ME_KEY = 'signIn_rememberMe';
const SAVED_EMAIL_KEY = 'signIn_savedEmail';

export const SignIn: FC = () => {
  const [isLoading, setIsloading] = useState<boolean>(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { showError } = useHeroToast();
  const { signInWithEmail } = useAuthState();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const savedRemember = localStorage.getItem(REMEMBER_ME_KEY) === 'true';
      const savedEmail = localStorage.getItem(SAVED_EMAIL_KEY) ?? '';
      if (savedRemember && savedEmail) {
        setRememberMe(true);
        setEmail(savedEmail);
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  const validate = (): boolean => {
    let isValid = true;
    setEmailError(null);
    setPasswordError(null);

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setEmailError('E-mail is required');
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setEmailError('Enter a valid e-mail address');
      isValid = false;
    }

    if (!password) {
      setPasswordError('Password is required');
      isValid = false;
    } else if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      isValid = false;
    }

    return isValid;
  };

  const handleRememberMeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setRememberMe(checked);
    try {
      if (checked) {
        localStorage.setItem(REMEMBER_ME_KEY, 'true');
        if (email.trim()) {
          localStorage.setItem(SAVED_EMAIL_KEY, email.trim());
        }
      } else {
        localStorage.removeItem(REMEMBER_ME_KEY);
        localStorage.removeItem(SAVED_EMAIL_KEY);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!validate()) return;

    const trimmedEmail = email.trim();

    try {
      if (rememberMe) {
        localStorage.setItem(REMEMBER_ME_KEY, 'true');
        localStorage.setItem(SAVED_EMAIL_KEY, trimmedEmail);
      } else {
        localStorage.removeItem(REMEMBER_ME_KEY);
        localStorage.removeItem(SAVED_EMAIL_KEY);
      }
    } catch (error) {
      console.error(error);
    }

    setIsloading(true);
    try {
      await signInWithEmail(trimmedEmail, password);
      navigate('/');
    } catch (error: unknown) {
      setIsloading(false);
      const message = error instanceof Error ? error.message : null;
      const errorMessage = message || 'Something went wrong, please try again';
      showError(errorMessage, 'Sign-in error');
    }
  };

  const inputBase = 'w-full rounded-lg border px-4 py-3 text-sm outline-none transition '
    + 'text-gray-900 font-medium';
  const inputNormal = 'border-[#d4c5ab] focus:border-[#1a4d6d] bg-white';
  const inputError = 'border-red-400 focus:border-red-500 bg-red-50';

  return (
    <MainContainer>
      <FormContainer>
        <LoginBox>
          <LoginHeader>
            <img src="/logo-hotelspot.svg" alt="Logo" />
          </LoginHeader>

          <LoginContent>
            <WelcomeText>
              <h2>Admin Center</h2>
              <p>Sign in to continue.</p>
            </WelcomeText>

            <form onSubmit={handleSubmit} noValidate>
              <div className="flex flex-col gap-4">
                {/* Email */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="sign-in-email" className="text-sm font-medium text-[#1a4d6d]">
                    <span className="mb-1.5 block">E-mail</span>
                    <input
                      id="sign-in-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`${inputBase} ${emailError ? inputError : inputNormal}`}
                      autoComplete="email"
                    />
                  </label>
                  {emailError && (
                    <span className="text-xs text-red-600">{emailError}</span>
                  )}
                </div>

                {/* Password */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="sign-in-password" className="text-sm font-medium text-[#1a4d6d]">
                    <span className="mb-1.5 block">Password</span>
                    <div className="relative">
                      <input
                        id="sign-in-password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className={`${inputBase} pr-11 ${passwordError ? inputError : inputNormal}`}
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-gray-400 hover:text-[#1a4d6d] focus:outline-none transition-colors"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                      </button>
                    </div>
                  </label>
                  {passwordError && (
                    <span className="text-xs text-red-600">{passwordError}</span>
                  )}
                </div>

                {/* Remember me */}
                <label htmlFor="sign-in-remember" className="flex cursor-pointer items-center gap-2.5 select-none">
                  <div className="relative flex items-center">
                    <input
                      id="sign-in-remember"
                      type="checkbox"
                      checked={rememberMe}
                      onChange={handleRememberMeChange}
                      className="peer sr-only"
                    />
                    <div
                      className={`flex h-[18px] w-[18px] items-center justify-center rounded border-2 transition-colors
                        ${rememberMe
                        ? 'border-[#1a4d6d] bg-[#1a4d6d]'
                        : 'border-[#d4c5ab] bg-white hover:border-[#1a4d6d]'
                        }`}
                    >
                      {rememberMe && (
                        <svg
                          className="h-2.5 w-2.5 text-white"
                          viewBox="0 0 12 10"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="1 5 4.5 8.5 11 1" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-sm text-[#1a4d6d]">Remember me</span>
                </label>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#1a4d6d] to-[#2c5f7d] px-4 py-3 text-sm font-medium text-white shadow-md transition hover:from-[#1a4d6d] hover:via-[#2c5f7d] hover:to-[#c5a572] hover:shadow-lg disabled:opacity-70"
                >
                  {isLoading && <LoadingSpinner />}
                  <span>{isLoading ? 'Signing in...' : 'Sign in'}</span>
                </button>
              </div>
            </form>

            <InfoText>
              <Link to="/set-password" className="text-[#1a4d6d] underline">
                Forgot password
              </Link>
            </InfoText>
          </LoginContent>
        </LoginBox>
      </FormContainer>
    </MainContainer>
  );
};
